import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { useClickable } from '../hooks/use-clickable.js';
import { useDebugAttributes } from '../hooks/use-debug-attributes.js';
import { useOnValueChangeEffect } from '../hooks/use-on-value-change-effect.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { managerControlsActiveElement } from './tabs-primitive.utils.js';

function TabPrimitiveInner({ children, actionOnPointerDown, id, display, manager, onboardingKey, panelId, recordingKey, onKeyDown, triggerId, className, ...props }, ref) {
    const fplData = useDebugAttributes({ descendant: 'tab' });
    const buttonProps = useClickable({
        onClick(e) {
            // Updating the tabIndex directly here allows the browser to natively focus this element
            // which allows it to have the correct `focus-visible` state.
            e.currentTarget.tabIndex = 0;
            manager.setActiveTab(id, true);
        },
        onKeyDown,
        recordingKey: display ? recordingKey : undefined,
        actionOnPointerDown,
        'data-onboarding-key': onboardingKey,
        ...props,
    });
    const triggerRef = useSharedRef(ref);
    const isActive = manager.activeTab === id;
    function hasTabIndex() {
        const noneActive = manager.activeTab == null;
        const isFirstTab = manager.tabs[0] === id;
        return isActive || (noneActive && isFirstTab);
    }
    useOnValueChangeEffect(() => {
        // only focus this tab if another tab in the group is already focused
        if (isActive && managerControlsActiveElement(manager)) {
            triggerRef.current?.focus();
        }
    }, isActive);
    if (!display)
        return null;
    return (jsx("button", { "data-fpl-component": "primitive", ...fplData, ...buttonProps, type: "button", className: className, role: "tab", "aria-selected": id === manager.activeTab, "aria-controls": panelId, id: triggerId, ref: triggerRef, tabIndex: hasTabIndex() ? 0 : -1, children: children }));
}
TabPrimitiveInner.displayName = 'TabsPrimitive.Tab';
/**
 * TabsPrimitive.Tab is an unstyled (“headless”) component used inside a TabStrip.
 * Use `TabsPrimitive.Tab` only if you need custom styling; otherwise, use
 *  `Tabs.Tab`. @see https://fpl.figma.design/components/tabs/
 */
const TabPrimitive = forwardRef(TabPrimitiveInner);

export { TabPrimitive };
//# sourceMappingURL=tab-primitive.js.map
